<template>
    <div v-if="canLoadHistory">
        <q-banner v-if="loading" rounded class="bg-grey-2 text-grey-9">
            <q-spinner size="sm" rounded class="q-mr-sm" />Loading form history…
        </q-banner>
        <q-banner v-else-if="error" rounded class="bg-red-1 text-warning">
            {{ error }}
        </q-banner>
        <q-banner v-else-if="!previousForms.length" rounded class="bg-grey-2 text-grey-9">
            {{ emptyMessage }}
        </q-banner>
        <q-table v-else class="form-table" flat bordered dense :rows="previousForms" :columns="columns"
            row-key="__key" :pagination="tablePagination">
            <template v-slot:body="props">
                <q-tr
                    :props="props"
                    :class="rowClass(props.row)"
                >
                    <q-td v-for="column in dataColumns" :key="column.name" :props="props">
                        <q-badge
                            v-if="column.name === 'status'"
                            :color="statusColor(props.row)"
                            :label="capitalise(props.row.status)"
                            outline
                            class="text-weight-medium"
                        >
                            <q-tooltip v-if="props.row.status === 'approved' && props.row.approved_by_name" class="text-body2">
                                Approved by {{ props.row.approved_by_name }}
                            </q-tooltip>
                        </q-badge>
                        <template v-else>{{ props.row[column.field] }}</template>
                    </q-td>
                    <q-td key="actions" :props="props">
                        <div v-if="isInvitation(props.row)" class="text-caption text-grey-7">
                            Expires {{ props.row.expires_at }}
                        </div>
                        <div v-else class="row justify-end q-gutter-sm nowrap">
                            <q-btn v-if="canAssign"
                                flat
                                class="q-pr-xs q-pl-sm icon-hover"
                                dense
                                no-caps
                                size="md"
                                color="secondary"
                                icon="o_person_add"
                                @click="onAssignClient(props.row)"
                            >
                                <q-tooltip class="text-body2">Assign to Client</q-tooltip>
                            </q-btn>
                            <q-btn v-if="allowView"
                                flat
                                class="q-pr-xs q-pl-sm icon-hover"
                                dense
                                no-caps
                                size="md"
                                color="primary"
                                :icon="isComplete(props.row) ? 'o_visibility' : 'o_edit'"
                                @click="onEditView(props.row)"
                            >
                                <q-tooltip class="text-body2">{{ isComplete(props.row) ? 'View Response' : 'Edit Response' }}</q-tooltip>
                            </q-btn>
                            <q-btn v-if="allowDownload"
                                flat
                                class="q-pr-xs q-pl-sm icon-hover"
                                dense
                                no-caps
                                size="md"
                                color="accent"
                                icon="download"
                                :loading="downloadingId === props.row.formResponseId"
                                :disable="downloadingId === props.row.formResponseId"
                                @click="onDownloadPdf(props.row)"
                            >
                                <q-tooltip class="text-body2">Download PDF</q-tooltip>
                            </q-btn>
                            <q-btn v-if="allowDelete"
                                flat
                                class="q-pr-sm q-pl-sm icon-hover"
                                dense
                                no-caps
                                size="md"
                                icon="o_delete"
                                color="negative"
                            >
                                <q-tooltip class="text-body2">Delete Response</q-tooltip>
                                <q-menu anchor="bottom right" self="top right">
                                    <q-card class="q-pa-sm" style="min-width: 280px">
                                        <q-card-section class="text-h6">Delete Response?</q-card-section>
                                        <q-card-section class="text-body2">
                                            Are you sure you want to delete this form response? This action cannot be undone.
                                        </q-card-section>
                                        <q-card-actions align="right">
                                            <q-btn
                                                flat
                                                label="Cancel"
                                                color="primary"
                                                v-close-popup
                                            />
                                            <q-btn
                                                flat
                                                label="Delete"
                                                color="warning"
                                                :loading="deleting"
                                                @click="confirmDelete(props.row)"
                                            />
                                        </q-card-actions>
                                    </q-card>
                                </q-menu>
                            </q-btn>
                        </div>
                    </q-td>
                </q-tr>
            </template>
        </q-table>
        <span v-if="currentResponseId" class="text-caption text-grey-8">
            The currently-open response is highlighted in blue unless it is highlighted for the selected engagement.
        </span>
    </div>
</template>

<script>
    import api from "@/services/api/api"
    import notify from "@/services/util/notify";
    import token from "@/services/util/roleHandler";
    import { isPaua, isPersonScoped, subjectFromResponse, subjectWithoutClientEngagement, ANONYMOUS_NAME, ANONYMOUS_LABEL } from "@/services/util/formSubject";


    export default {
        name: "PreviousFormsTable",
        emits: ['form-selected', 'form-deleted', 'exit-form', 'assign-client'],
        props: {
            // The form subject ({ source, id, name, ... }) the history is scoped to.
            subject: {
                type: [Object, null],
                default: null
            },
            selectedFormId: {
                type: [Number, String, null],
                default: null
            },
            // Allows the table to load a response set spanning multiple form types.
            historyLoader: {
                type: Function,
                default: null
            },
            displayColumns: {
                type: Array,
                default: () => ['subject', 'version', 'updated', 'status']
            },
            rowsPerPage: {
                type: Number,
                default: 10
            },
            emptyMessage: {
                type: String,
                default: 'No previous forms were found.'
            },
            currentResponseId: {
                type: [Number, String, null],
                default: null
            },
            highlightClientEngagementId: {
                type: [Number, String, null],
                default: null
            },
            highlight: {
                type: Boolean,
                default: true
            },
            // Whether non-Paua responses may be assigned to a real client from
            // this table. Requires the parent to handle the 'assign-client' event.
            allowAssign: {
                type: Boolean,
                default: true
            },
            allowView: {
                type: Boolean,
                default: true
            },
            allowDownload: {
                type: Boolean,
                default: true
            },
            allowDelete: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                loading: false,
                error: null,
                previousForms: [],
                deleting: false,
                downloadingId: null,
                userInfo: token.getTokenFields(),
            }
        },
        computed: {
            canLoadHistory() {
                return this.selectedFormId !== null || !!this.historyLoader;
            },
            // True when the history is scoped to one specific person (Paua client
            // or tk_person) — the first column then shows the start date.
            personScoped() {
                return isPersonScoped(this.subject);
            },
            historySubject() {
                return subjectWithoutClientEngagement(this.subject);
            },
            highlightedClientEngagementId() {
                return this.highlightClientEngagementId
                    ?? this.subject?.clientEngagementId
                    ?? this.subject?.client_engagement_id
                    ?? null;
            },
            // Non-Paua subjects can have their responses assigned to a real client.
            canAssign() {
                return this.allowAssign && !!this.subject && !isPaua(this.subject);
            },
            columnDefinitions() {
                return {
                    subject: this.personScoped
                        ? { name: 'started_at', label: 'Started', field: 'started_at' }
                        : { name: 'client_full_name', label: 'Client', field: 'client_full_name' },
                    version: { name: 'version', label: 'Version', field: 'version' },
                    form: { name: 'form_title', label: 'Form', field: 'form_title' },
                    started: { name: 'started_at', label: 'Started', field: 'started_at' },
                    updated: { name: 'updated_at', label: 'Updated', field: 'updated_at' },
                    completed: { name: 'completed_on', label: 'Completed', field: 'completed_on' },
                    completedBy: { name: 'completed_by', label: 'Completed By', field: 'completed_by' },
                    status: { name: 'status', label: 'Status', field: 'status', align: 'right' }
                };
            },
            dataColumns() {
                const columns = this.displayColumns.filter(name => name !== 'version');
                if (this.previousForms.some(form => form.version !== '-')) columns.splice(1, 0, 'version');
                return columns
                    .map(name => this.columnDefinitions[name])
                    .filter(Boolean)
                    .map(column => ({ align: 'left', sortable: true, ...column }));
            },
            columns() {
                return [
                    ...this.dataColumns,
                    { name: 'actions', label: 'Actions', field: 'actions', align: 'right', sortable: false }
                ];
            },
            tablePagination() {
                const sortBy = this.displayColumns.includes('completed') ? 'completed_on' : 'status';
                return { rowsPerPage: this.rowsPerPage, sortBy, descending: true };
            }
        },
        watch: {
            subject() {
                this.previousForms = [];
                this.error = null;
                this.fetchPreviousForms();
            },
            selectedFormId() {
                this.previousForms = [];
                this.error = null;
                this.fetchPreviousForms();
            }
        },
        mounted() {
            this.fetchPreviousForms();
        },
        methods: {
            async fetchPreviousForms() {
                if (!this.canLoadHistory) {
                    this.previousForms = [];
                    return;
                }
                this.loading = true;
                this.error = null;
                this.previousForms = [];
                try {

                    const res = this.historyLoader
                        ? await this.historyLoader()
                        : await api.forms.getFormHistory(
                            this.selectedFormId,
                            this.historySubject,
                            this.userInfo?.role === 'staff' ? this.userInfo?.email : undefined
                        );
                    const data = Array.isArray(res)
                        ? res
                        : (res?.data?.data || res?.data || []);
                    this.previousForms = data.map((item, idx) => ({
                        __key: item?.record_type === 'invitation'
                            ? `invitation-${item.invitation_id}`
                            : `response-${item?.id || idx}-${item?.updated_at || ""}`,
                        recordType: item?.record_type || 'response',
                        invitationId: item?.invitation_id,
                        client_full_name: this.getClientFullName(item),
                        formResponseId: item?.id,
                        formId: item?.form_id,
                        version: item?.family_id ? `v${item.version_number}` : '-',
                        clientEngagementId: item?.client_engagement_id ?? null,
                        form_title: item?.form_title || `Form ${item?.form_id}`,
                        started_at: this.formatDate(item?.started_at),
                        updated_at: this.formatDate(item?.updated_at),
                        completed_on: this.formatDate(item?.completed_on),
                        expires_at: this.formatDate(item?.expires_at),
                        completed_by: item?.staff_id || '-',
                        approved_by_name: item?.approved_by_name || null,
                        status: item?.status || '-'
                    }));
                } catch (e) {
                    this.error = 'Failed to load previous forms.';
                } finally {
                    this.loading = false;
                }
            },
            getClientFullName(item) {
                const explicitName = item?.client_full_name;
                if (explicitName) return explicitName;

                const firstName = item?.client_first_name || item?.first_name;
                const lastName = item?.client_last_name || item?.last_name;
                if (firstName || lastName) return `${firstName || ''} ${lastName || ''}`.trim();

                const temporaryName = item?.temporary_client_name;
                if (temporaryName) return temporaryName;

                // A nameless tk_person and an anonymous-source response are both
                // legitimately "Anonymous"; a Paua row with no name is just
                // missing data, shown as '-'.
                const subject = subjectFromResponse(item);
                if (subject?.source === 'anonymous') return ANONYMOUS_LABEL;
                return subject?.source === 'tk_person' ? ANONYMOUS_NAME : '-';
            },
            formatDate(dateStr) {
                if (!dateStr) return '-';
                const date = new Date(dateStr);
                return isNaN(date.getTime()) ? '-' : date.toLocaleString('en-nz');
            },
            onEditView(row) {
                if (row?.formResponseId) {
                    this.$emit('form-selected', row.formResponseId, row.formId);
                }
            },
            onAssignClient(row) {
                if (!row?.formResponseId) return;
                this.$emit('assign-client', row.formResponseId);
            },
            async onDownloadPdf(row) {
                const formId = row?.formId || this.selectedFormId;
                if (!row?.formResponseId || !formId) return;
                this.downloadingId = row.formResponseId;
                try {
                    await api.forms.exportFormResponsePdf(
                        formId,
                        row.formResponseId,
                        this.subject?.name
                    );
                } catch (e) {
                    console.error(e);
                    notify.error('Failed to download PDF', 'top');
                } finally {
                    this.downloadingId = null;
                }
            },
            async confirmDelete(row) {
                const formId = row?.formId || this.selectedFormId;
                if (!row?.formResponseId || !formId) return;
                this.deleting = true;
                const isDeletingCurrentResponse = row.formResponseId === this.currentResponseId;

                try {
                    await api.forms.deleteDynamicForm(formId, row.formResponseId);
                    this.previousForms = this.previousForms.filter(form => form.formResponseId !== row.formResponseId);
                    this.$emit('form-deleted', row.formResponseId, formId);
                    notify.primary('Form deleted successfully', 'top');

                    // If we deleted the currently-viewed response, navigate back
                    if (isDeletingCurrentResponse) {
                        this.$emit('exit-form');
                    }
                } catch (e) {
                    console.error(e);
                    notify.error('Failed to delete form', 'top');
                } finally {
                    this.deleting = false;
                }
            },
            /**
             * Format an enum-style string; split by underscores and capitalise each word.
             */
            capitalise(string) {
                if (typeof string !== 'string') return '';
                return string
                    .replace(/_/g, ' ')
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(' ');
            },
            isComplete(row) {
                return row.completed_on !== '-' && row.completed_on !== null;
            },
            isInvitation(row) {
                return row.recordType === 'invitation';
            },
            statusColor(row) {
                if (row.status === 'awaiting_approval') return 'warning';
                if (row.status === 'approved') return 'positive';
                if (row.status === 'sent_to_client_email') return 'secondary';
                if (row.status === 'external_link_expired' || row.status === 'external_link_revoked') return 'warning';
                return this.isComplete(row) ? 'positive' : 'grey';
            },
            isCurrentResponse(row) {
                return row.formResponseId === this.currentResponseId;
            },
            isEngagementResponse(row) {
                if (this.highlightedClientEngagementId === null || row.clientEngagementId == null) return false;
                return String(row.clientEngagementId) === String(this.highlightedClientEngagementId);
            },
            rowClass(row) {
                if (this.highlight && this.isEngagementResponse(row)) return 'form-table__row--engagement';
                return this.isCurrentResponse(row) ? 'bg-blue-1' : '';
            }
        }
    }
</script>

<style scoped>
:deep(.form-table__row--engagement > td) {
    background: #e8f5e9 !important;
}

:deep(.form-table__row--engagement > td:first-child) {
    border-left: 4px solid var(--q-positive);
}

</style>
